import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import {
  ProjectPropertiesMenuItem,
  ProjectPropertiesMenuItemPrefix,
  ProjectPropertiesMenuItemValue,
} from '@/features/projects/project-details/components/layouts/project-properties-menu-layout';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { AvatarsGroup } from '@/shared/components/ui/avatars-group';
import { ContractorWithAlias, ProjectType } from '@/shared/types/api';
import { getInitials } from '@/shared/utils/avatar-util';

// Add a lightweight type for pending invitations
export type InvitedContractor = {
  id: string;
  alias?: string | null;
  to_email?: string | null;
};

interface ContractorsItemProps {
  contractors: ContractorWithAlias[];
  invitedContractors: InvitedContractor[];
  projectId?: number;
  projectType?: ProjectType;
}

export const ContractorsItem = ({
  contractors,
  invitedContractors,
  projectId,
  projectType,
}: ContractorsItemProps) => {
  const { t: tShared } = useTranslation('project', { keyPrefix: 'Shared' });
  const navigate = useNavigate();

  const isEditable = projectType === 'subproject_client';

  const handleEdit = () => {
    if (projectId) {
      navigate(paths.projects.details.contractorManagement.getHref(projectId));
    }
  };

  return (
    <ProjectPropertiesMenuItem isEditable={isEditable} onEdit={handleEdit}>
      <ProjectPropertiesMenuItemPrefix>
        {tShared('contractors')}
      </ProjectPropertiesMenuItemPrefix>
      <ProjectPropertiesMenuItemValue>
        <div className="flex items-center gap-2">
          {[...contractors, ...invitedContractors].length > 0 ? (
            <AvatarsGroup>
              {contractors.map((c) => (
                <ContractorAvatar
                  key={c.id}
                  name={c.partner_alias.alias}
                  imageUrl={c.partner_alias.partner_org?.image_url}
                />
              ))}
              {invitedContractors.map((c) => (
                <ContractorAvatar
                  key={c.id}
                  name={c.alias ?? c.to_email ?? '—'}
                />
              ))}
            </AvatarsGroup>
          ) : (
            <span>{tShared('no_contractors')}</span>
          )}
        </div>
      </ProjectPropertiesMenuItemValue>
    </ProjectPropertiesMenuItem>
  );
};

const ContractorAvatar = ({
  key,
  name,
  imageUrl,
}: {
  key: string;
  name: string | null | undefined;
  imageUrl?: string | null;
}) => (
  <Avatar key={key} className="size-7 border">
    {imageUrl ? (
      <img
        src={imageUrl}
        alt={name ?? 'Contractor'}
        className="size-full object-cover"
      />
    ) : (
      <AvatarFallback>{getInitials(name ?? '?')}</AvatarFallback>
    )}
  </Avatar>
);
